import React, { useState, useEffect } from "react";
import { Container, Grid, Typography, InputLabel, FormControl, Select, MenuItem, Button, LinearProgress, Link } from "@material-ui/core";
import { GridTextField, HelpscoutFab } from "components";
import { listTimeZones } from "timezone-support";
import { useSnackbar } from "notistack";
import { getSportForms, SportForm } from "api/courts";
import { getCoordinatesByAddress } from "utils/geocode";
import { UserProfile } from "api/user";
import { getClubForSignup, postClubOwnerSignup } from "api/owner/settings";
import uI18n from "utils/i18n";
import i18n from "./i18n";
import RegisterClubView from "./RegisterClubView";

// prop types
interface OwnerSignupViewProps {
    isCompany?: boolean,
    isAuthenticated: boolean,
    token?: string,
    urlData: { [x: string]: string },
    userProfile: UserProfile | null,
    onFetchUserProfile: () => void,
    onNavigateToCalendar: () => void,
    onShowLoginDialog: () => void,
    onSignOut: () => void,
    onSignUp: (email: string, password: string, firstName: string, lastName: string) => void,
    onResendConfirmationCode: (email: string) => void,
    onConfirmUser: (email: string, password: string, firstName: string, lastName: string, code: string, clubId?: string) => void,
}

// owner signup form view
const OwnerSignupView = ({ isCompany, isAuthenticated, token, urlData, userProfile, onFetchUserProfile, onNavigateToCalendar, onShowLoginDialog, onSignOut, onSignUp, onResendConfirmationCode, onConfirmUser }: OwnerSignupViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [addresseeName, setAddresseeName] = useState("");
    const [city, setCity] = useState("");
    const [clubKey, setClubKey] = useState("");
    const [clubName, setClubName] = useState("");
    const [country, setCountry] = useState("");
    const [generalSportForms, setGeneralSportForms] = useState<SportForm[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [latitude, setLatitude] = useState(0);
    const [longitude, setLongitude] = useState(0);
    const [mainSportForm, setMainSportForm] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [shortName, setShortName] = useState("");
    const [stateOrProvince, setStateOrProvince] = useState("");
    const [street, setStreet] = useState("");
    const [timeZone, setTimeZone] = useState("");

    // fetch data
    const fetchGeneralSportForms = async () => { setGeneralSportForms(await getSportForms()); };
    const fetchClubData = async () => {
        setLoading(true);
        try {
            await fetchGeneralSportForms();
            const club = await getClubForSignup({ signupToken: token! });
            setAddresseeName(club.addresseeName);
            setCity(club.city);
            setClubKey(club.clubKey || "");
            setClubName(club.clubName);
            setCountry(club.country);
            setLatitude(club.latitude);
            setLongitude(club.longitude);
            setMainSportForm(club.mainSportForm);
            setPostalCode(club.postalCode);
            setShortName(club.shortName);
            setStateOrProvince(club.stateOrProvince);
            setStreet(club.street);
            setTimeZone(club.timeZone);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };
    useEffect(() => { if (token) fetchClubData(); }, [token]); // eslint-disable-line

    // show login
    useEffect(() => { if (!isAuthenticated && token) onShowLoginDialog(); }, [isAuthenticated, token]); // eslint-disable-line

    // execute action
    const execute = async () => {
        setLoading(true);
        try {
            await postClubOwnerSignup({
                addresseeName,
                city,
                clubKey,
                clubName,
                country,
                isPublic: true,
                latitude,
                longitude,
                mainSportForm: (isCompany ? "multisport" : mainSportForm),
                postalCode,
                shortName,
                signupToken: token!,
                stateOrProvince,
                street,
                timeZone,
            });
            const registerSuccess = isCompany ? i18n.registerCompanySuccess : i18n.registerSuccess;
            enqueueSnackbar(registerSuccess, { variant: "success" });
            onFetchUserProfile(); // refresh clubs
            onNavigateToCalendar();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // update coordinates by geocoding address
    const updateCoordinates = async () => {
        const address = `${street} ${postalCode} ${city} ${stateOrProvince} ${country}`;
        const result = await getCoordinatesByAddress(address);
        setLatitude(result.latitude);
        setLongitude(result.longitude);
    };

    // render
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    return !token ? (
        <RegisterClubView
            isCompany={isCompany}
            urlData={urlData}
            userProfile={userProfile}
            onShowLoginDialog={onShowLoginDialog}
            onSignOut={onSignOut}
            onSignUp={onSignUp}
            onResendConfirmationCode={onResendConfirmationCode}
            onConfirmUser={onConfirmUser}
        />
    ) : (
        <Container maxWidth="sm" style={{ marginTop: "32px" }}>
            {!isCompany && (<HelpscoutFab />)}
            <Grid container spacing={2}>

                {/* header */}
                <Grid item xs={12} style={{ textAlign: "center" }}>
                    <img src={isCompany ? "images/playsports-move_720px.png" : "images/playsports_logo.svg"} alt="PLAYSPORTS" style={{ height: "96px", margin: "32px" }} />
                    <Typography variant="h6">{i18n.registerCompany}</Typography>
                    {userProfile && (
                        <Typography>
                            {`${i18n.loggedInAs(userProfile.email)} (`}
                            <Link onClick={onSignOut} style={{ cursor: "pointer" }}>{i18n.useOtherAccount}</Link>
                            )
                        </Typography>
                    )}
                </Grid>

                {/* club fields */}
                <GridTextField size={12} label={isCompany ? i18n.companyName : i18n.clubName} value={clubName} onChange={setClubName} />
                <GridTextField size={6} label={i18n.shortName} maxLength={8} value={shortName} onChange={setShortName} />
                <GridTextField size={6} label={isCompany ? i18n.CompanyKey : i18n.clubKey} maxLength={36} value={clubKey} onChange={setClubKey} />
                {!isCompany && (
                    <Grid item xs={6}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>{i18n.mainSportForm}</InputLabel>
                            <Select
                                label={i18n.mainSportForm}
                                value={mainSportForm}
                                onChange={(e) => setMainSportForm(e.target.value as string)}
                            >
                                {generalSportForms.map((t) => (
                                    <MenuItem key={t.name} disabled={!t.available} value={t.name}>{uI18n.sportForms[t.name] || capitalize(t.name)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={isCompany ? 12 : 6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.timeZone}</InputLabel>
                        <Select
                            label={i18n.timeZone}
                            value={timeZone}
                            onChange={(e) => setTimeZone(e.target.value as string)}
                        >
                            {listTimeZones().map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <GridTextField size={4} label={i18n.latitude} value={latitude.toString()} onChange={(v) => setLatitude(parseFloat(v))} />
                <GridTextField size={4} label={i18n.longitude} value={longitude.toString()} onChange={(v) => setLongitude(parseFloat(v))} />
                <Grid item xs={4} style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={updateCoordinates}>
                        {i18n.fromAddress}
                    </Button>
                </Grid>

                {/* addressee fields */}
                <GridTextField size={6} label={i18n.addresseeName} value={addresseeName} onChange={setAddresseeName} />
                <GridTextField size={6} label={i18n.street} value={street} onChange={setStreet} />
                <GridTextField size={6} label={i18n.postalCode} value={postalCode} onChange={setPostalCode} />
                <GridTextField size={6} label={i18n.city} value={city} onChange={setCity} />
                <GridTextField size={6} label={i18n.stateOrProvince} value={stateOrProvince} onChange={setStateOrProvince} />
                <GridTextField size={6} label={i18n.country} value={country} onChange={setCountry} />

                {/* loading & button */}
                <Grid item xs={12}>
                    {isLoading && <LinearProgress />}
                </Grid>
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" color="primary" disabled={isLoading || !token} onClick={isAuthenticated ? execute : onShowLoginDialog}>
                        {isAuthenticated ? i18n.save : i18n.login}
                    </Button>
                </Grid>
            </Grid>
        </Container>
    );
};

export default OwnerSignupView;
